
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRenderLayerManager } from './render-layer-context';
import { RenderLayer } from './render-layer.enum';

/**
 * Main component that renders all registered render layer nodes
 * Automatically applies viewport transformations to all nodes
 * Uses throttled updates based on refreshInterval (FPS limit)
 */
export const RenderLayers: React.FC<{ layerVisibility?: Record<RenderLayer, boolean> }> = ({ layerVisibility }) => {
	const renderLayerManager = useRenderLayerManager();
	const [version, setVersion] = useState(() => renderLayerManager.getVersion());
	const lastRenderTime = useRef(0);
	const pendingUpdate = useRef(false);

	// Throttled update function
	const throttledUpdate = useCallback(() => {
		const now = performance.now();
		const minInterval = 1000 / renderLayerManager.config.refreshInterval; // Convert FPS to milliseconds

		// console.log('RenderLayers: throttledUpdate called', {
		// 	now,
		// 	lastRenderTime: lastRenderTime.current,
		// 	minInterval,
		// 	fps: renderLayerManager.config.refreshInterval,
		// 	timeSinceLastRender: now - lastRenderTime.current,
		// 	shouldUpdate: now - lastRenderTime.current >= minInterval
		// });

		if (now - lastRenderTime.current >= minInterval) {
			// Enough time has passed, update immediately
			const currentVersion = renderLayerManager.getVersion();
			if (currentVersion !== version) {
				console.log('RenderLayers: version changed (immediate)', { old: version, new: currentVersion });
				setVersion(currentVersion);
				lastRenderTime.current = now;
				// Increment render count only when we actually render due to throttled update
				renderLayerManager.incrementRenderCount();
			}
			pendingUpdate.current = false;
		} else if (!pendingUpdate.current) {
			// Schedule update for later
			pendingUpdate.current = true;
			const remainingTime = minInterval - (now - lastRenderTime.current);
			// console.log('RenderLayers: scheduling throttled update', { remainingTime, minInterval });
			setTimeout(() => {
				if (pendingUpdate.current) {
					const currentVersion = renderLayerManager.getVersion();
					if (currentVersion !== version) {
						// console.log('RenderLayers: version changed (throttled)', { old: version, new: currentVersion });
						setVersion(currentVersion);
						lastRenderTime.current = performance.now();
						// Increment render count only when we actually render due to throttled update
						renderLayerManager.incrementRenderCount();
					}
					pendingUpdate.current = false;
				}
			}, remainingTime);
		}
	}, [renderLayerManager, version]);

	// Subscribe to changes and use throttled updates
	useEffect(() => {
		const unsubscribe = renderLayerManager.subscribe(throttledUpdate);
		return unsubscribe;
	}, [renderLayerManager, throttledUpdate]);

	// Force initial render and periodic checks to ensure rendering
	useEffect(() => {
		// Initial render check
		const currentVersion = renderLayerManager.getVersion();
		if (currentVersion !== version) {
			console.log('RenderLayers: initial version update', { old: version, new: currentVersion });
			setVersion(currentVersion);
		}

		// Note: Removed fallback polling as it was interfering with throttling
		// The throttled update mechanism should handle all updates properly
	}, [renderLayerManager, version]);

	const allNodes = renderLayerManager.getSortedNodes();

	// Filter nodes based on layer visibility if provided
	const nodes = layerVisibility
		? allNodes.filter((node) => layerVisibility[node.layer])
		: allNodes;

	// Debug logging
	// useEffect(() => {
	// 	console.log('RenderLayers: rendering nodes', {
	// 		totalNodes: nodes.length,
	// 		version,
	// 		refreshInterval: renderLayerManager.config.refreshInterval,
	// 		fps: renderLayerManager.config.refreshInterval
	// 	});

	// 	// Log nodes by layer
	// 	const layerStats = renderLayerManager.getLayerStats();
	// 	console.log('RenderLayers: layer stats', Object.fromEntries(layerStats));

	// 	// Log individual nodes
	// 	nodes.forEach((node) => {
	// 		console.log('RenderLayers: node', { id: node.id, layer: node.layer, priority: node.priority });
	// 	});
	// }, [nodes, version, renderLayerManager]);

	return (
		<>
			{/* Debug info
			<text x="0" y="-200" fontSize="12" fill="red" textAnchor="middle">
				Debug: {nodes.length} nodes, version {version}
			</text> */}

			{nodes.map((node) => {
				// Automatically transform coordinates using viewport context
				// const transformedElement = transformElement(node.element);

				return (
					<React.Fragment key={node.id}>
						{node.element}
					</React.Fragment>
				);
			})}
		</>
	);
};

/**
 * Component for rendering only a specific layer
 * Useful for performance optimization when you only need certain layers
 */
export const RenderLayerComponent: React.FC<{ layer: RenderLayer }> = ({ layer }) => {
	const renderLayerManager = useRenderLayerManager();
	const [version, setVersion] = useState(() => renderLayerManager.getVersion());

	useEffect(() => {
		const interval = setInterval(() => {
			const currentVersion = renderLayerManager.getVersion();
			if (currentVersion !== version) {
				setVersion(currentVersion);
			}
		}, renderLayerManager.config.refreshInterval);

		return () => clearInterval(interval);
	}, [renderLayerManager, version]);

	const nodes = renderLayerManager.getNodesByLayer(layer);

	return (
		<>
			{nodes.map((node) => {
				// Automatically transform coordinates using viewport context
				// const transformedElement = transformElement(node.element);

				return (
					<React.Fragment key={node.id}>
						{node.element}
					</React.Fragment>
				);
			})}
		</>
	);
};
